// ============================================================
// 旅行費用のロジック（純関数のみ・副作用なし）
// UI（TripMoneyView）とテストハーネス（scripts/test_trip_logic.js）の両方から使う。
// 日付判定に new Date() の toISOString を使わない（UTCズレ防止・既存 appDate 流儀）
// ============================================================

// ローカル日付 'YYYY-MM-DD'
export function localDateStr(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ローカル日時 'YYYY-MM-DD HH:mm'（実費レコードの at 用）
export function localDateTimeStr(d = new Date()) {
  return `${localDateStr(d)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// 金額の表示（¥1,234。負値は -¥1,234）
export function formatYen(n) {
  const v = Math.trunc(Number(n) || 0);
  const abs = Math.abs(v).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${v < 0 ? '-' : ''}¥${abs}`;
}

// 後方互換：trip / budgets / expenses が無い旧ドキュメントを安全に読む
export function getTrip(set) {
  return set && set.trip && typeof set.trip === 'object' ? set.trip : null;
}
export function getBudgets(set) {
  return set && Array.isArray(set.budgets) ? set.budgets : [];
}
export function getExpenses(set) {
  return set && Array.isArray(set.expenses) ? set.expenses : [];
}

// 旅行名（trip.tripName）。未設定・空白のみならセット名にフォールバック（後方互換）
// ヘッダ表示・エクスポートの「旅行の名称」の両方でこの1関数を使う
export function tripDisplayName(set) {
  const trip = getTrip(set);
  const n = trip && typeof trip.tripName === 'string' ? trip.tripName.trim() : '';
  return n || (set && set.name) || '';
}

// 費用設計済みか（💰ボタンの表示分岐に使う）
export function hasBudgetPlan(set) {
  return getBudgets(set).some((b) => (Number(b.amount) || 0) > 0) || getExpenses(set).length > 0;
}

// ============================================================
// テーマ（v1.11.0）：旅行の方向性を決める「メインテーマ」「サブテーマ」
//   trip.mainTheme / trip.subTheme（複数行テキスト・1行=1項目・任意フィールド）
//   trip の中に置くため、枠1=ルート／枠2・3=tripSlots 配下の既存パターンに自然に乗る
//   （旧データは undefined → '' 扱い＝移行不要の後方互換）
// ============================================================

// テーマ本文の取得（key: 'mainTheme' | 'subTheme'。未設定・旧データは ''）
export function tripTheme(set, key) {
  const trip = getTrip(set);
  return trip && typeof trip[key] === 'string' ? trip[key] : '';
}

// 複数行テキスト → 項目配列（行ごとに trim・空行は除去）
export function themeLines(text) {
  return String(text || '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

// 保存用の正規化：行整理して結合。実質空なら null（＝未設定に戻す）
export function normalizeThemeText(text) {
  const lines = themeLines(text);
  return lines.length > 0 ? lines.join('\n') : null;
}

// 費目ごとの実費合計 Map（catId → 合計円）
export function sumExpensesByCat(expenses) {
  const map = {};
  (expenses || []).forEach((e) => {
    if (!e || typeof e.catId !== 'string') return;
    const amt = Math.trunc(Number(e.amount) || 0);
    map[e.catId] = (map[e.catId] || 0) + amt;
  });
  return map;
}

// 全体集計 { budgetTotal, actualTotal, remaining, pct }
export function calcOverall(set) {
  const budgets = getBudgets(set);
  const expenses = getExpenses(set);
  const budgetTotal = budgets.reduce((n, b) => n + (Math.trunc(Number(b.amount)) || 0), 0);
  const actualTotal = expenses.reduce((n, e) => n + (Math.trunc(Number(e.amount)) || 0), 0);
  const remaining = budgetTotal - actualTotal;
  const pct = budgetTotal > 0 ? Math.min(100, Math.round((actualTotal / budgetTotal) * 100)) : 0;
  return { budgetTotal, actualTotal, remaining, pct };
}

// 費目の色（赤＝事業寄り／青＝private寄り／白＝両方・中立）を正規化
// 不正値・未設定（旧データ）は 'white'（無色）に落とす
export function normalizeColor(c) {
  return c === 'red' || c === 'blue' ? c : 'white';
}

// ============================================================
// 費目の表示順（v1.13.0）
//   費目マスタ（settings/expenseCategories の categories 配列）の**並び順そのもの**を
//   表示順の唯一の正とする。order 等の新フィールドは持たない＝既存データの移行は不要
//   （既存マスタの配列順＝これまでの表示順なので、並べ替えるまで見た目は一切変わらない）。
//   マスタはユーザーにつき1ドキュメント＝宿泊/日帰りの各セット・プラン枠1〜3で共通。
//   よって並べ替えも全プラン共通で効く（マスタが共通なので順序も共通が自然）。
// ============================================================

// 配列の index 番目を dir（-1=上へ / +1=下へ）に1つ動かした新しい配列を返す。
// 端を越える移動・不正な index は「元と同じ内容の新しい配列」を返す（呼び出し側で分岐不要）
export function moveItem(list, index, dir) {
  const arr = Array.isArray(list) ? list.slice() : [];
  const to = index + dir;
  if (!Number.isInteger(index) || index < 0 || index >= arr.length) return arr;
  if (to < 0 || to >= arr.length) return arr;
  const [item] = arr.splice(index, 1);
  arr.splice(to, 0, item);
  return arr;
}

// 行（catId を持つオブジェクト）をマスタの並び順に並べ替える（安定ソート）。
// マスタに無い費目（マスタから削除済みだが予算・実費には残っている孤児）は
// 元の相対順を保ったまま末尾へ送る
export function sortRowsByMaster(rows, master) {
  const list = Array.isArray(rows) ? rows : [];
  const idx = new Map();
  (master || []).forEach((c, i) => {
    if (c && typeof c.id === 'string' && !idx.has(c.id)) idx.set(c.id, i);
  });
  const tail = idx.size + list.length + 1; // マスタ外は必ず末尾に来る十分大きな順位
  return list
    .map((r, i) => ({ r, i, k: idx.has(r && r.catId) ? idx.get(r.catId) : tail }))
    .sort((a, b) => a.k - b.k || a.i - b.i)
    .map((x) => x.r);
}

// 費目別の比較行を作る（比較表示・スプシ書き出し用）
// budgets に無い catId の実費（マスタ変更後の孤児）は master から名前を引いて足す
// （master にも無ければ「不明な費目」）。最後に**マスタの並び順**へ揃える（v1.13.0）
export function buildCompareRows(set, master) {
  const budgets = getBudgets(set);
  const expenses = getExpenses(set);
  const actualByCat = sumExpensesByCat(expenses);
  const seen = new Set();
  const rows = budgets.map((b) => {
    seen.add(b.catId);
    const actual = actualByCat[b.catId] || 0;
    const budget = Math.trunc(Number(b.amount)) || 0;
    return {
      catId: b.catId,
      name: b.name,
      icon: b.icon || '📦',
      color: normalizeColor(b.color),
      budget,
      actual,
      remaining: budget - actual,
    };
  });
  Object.keys(actualByCat).forEach((catId) => {
    if (seen.has(catId)) return;
    const m = (master || []).find((c) => c.id === catId);
    rows.push({
      catId,
      name: m ? m.name : '不明な費目',
      icon: m && m.icon ? m.icon : '❓',
      color: normalizeColor(m && m.color),
      budget: 0,
      actual: actualByCat[catId],
      remaining: -actualByCat[catId],
    });
  });
  return sortRowsByMaster(rows, master);
}

// バーの状態（predicate は機械判定形：予算内=ok / 90%超=warn / 超過=over）
export function barState(budget, actual) {
  if (budget <= 0) return actual > 0 ? 'over' : 'ok';
  if (actual > budget) return 'over';
  if (actual / budget > 0.9) return 'warn';
  return 'ok';
}

// バー幅（0〜100%。予算0で実費ありは100%）
export function barPct(budget, actual) {
  if (budget <= 0) return actual > 0 ? 100 : 0;
  return Math.min(100, Math.round((actual / budget) * 100));
}

// 予算の表示行 = budgets（スナップショット）∪ master の未収載費目（amount 0）
// 並び順は**マスタの並び順**に揃える（v1.13.0）。マスタから消えた費目だけ末尾に残る。
// 既存スナップショットの名前・アイコン・色は書き換えない（順序だけをマスタに従わせる）
export function mergeBudgetRows(master, budgets) {
  const rows = (budgets || []).map((b) => ({
    catId: b.catId,
    name: b.name,
    icon: b.icon || '📦',
    color: normalizeColor(b.color),
    amount: Math.trunc(Number(b.amount)) || 0,
  }));
  const seen = new Set(rows.map((r) => r.catId));
  (master || []).forEach((c) => {
    if (seen.has(c.id)) return;
    rows.push({ catId: c.id, name: c.name, icon: c.icon || '📦', color: normalizeColor(c.color), amount: 0 });
  });
  return sortRowsByMaster(rows, master);
}

// 指定日（'YYYY-MM-DD'）の実費レコード（新しい順）
export function expensesOfDay(expenses, dayStr) {
  return (expenses || [])
    .filter((e) => e && typeof e.at === 'string' && e.at.startsWith(dayStr))
    .slice()
    .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
}

// テンキー入力：現在文字列 + キー → 次の文字列（最大8桁・先頭0は持たない）
export function applyKeypad(current, key) {
  const cur = current || '';
  if (key === 'back') return cur.slice(0, -1);
  if (key === '00') {
    if (cur === '') return '';
    return (cur + '00').slice(0, 8);
  }
  if (/^[0-9]$/.test(key)) {
    if (cur === '' && key === '0') return '';
    return (cur + key).slice(0, 8);
  }
  return cur;
}

// ============================================================
// ポップアップ電卓（費目行タップで開くモーダル用・v1.9.2）
// 状態: { entry: 入力中の数字文字列, acc: 確定済み左辺(数値|null), op: 保留中の演算子(null|'+'|'-'|'*'|'/') }
// 金額は 0〜99,999,999 の整数に必ず収める（負値・小数はここで潰す）
// ============================================================
export const CALC_INIT = { entry: '', acc: null, op: null };

const CALC_MAX = 99999999;

// 2項演算（整数化・0〜上限にクランプ。÷0 は左辺をそのまま返す＝無視）
export function calcBinary(a, op, b) {
  let r;
  if (op === '+') r = a + b;
  else if (op === '-') r = a - b;
  else if (op === '*') r = a * b;
  else if (op === '/') r = b === 0 ? a : a / b;
  else r = b;
  return Math.max(0, Math.min(CALC_MAX, Math.trunc(r)));
}

// 電卓キーを1つ適用して次の状態を返す
// key: '0'-'9' | '00' | 'back' | '+' | '-' | '*' | '/' | '=' | 'clear'
export function applyCalcKey(state, key) {
  const s = state || CALC_INIT;
  if (key === 'clear') return { ...CALC_INIT };
  if (key === 'back' || key === '00' || /^[0-9]$/.test(key)) {
    return { ...s, entry: applyKeypad(s.entry, key) };
  }
  if (key === '+' || key === '-' || key === '*' || key === '/') {
    if (s.entry !== '') {
      const b = parseInt(s.entry, 10) || 0;
      const acc = s.acc === null ? Math.min(CALC_MAX, b) : calcBinary(s.acc, s.op, b);
      return { entry: '', acc, op: key };
    }
    if (s.acc !== null) return { ...s, op: key }; // 演算子の変更のみ
    return s; // 何も入っていなければ無視
  }
  if (key === '=') {
    if (s.acc !== null && s.op && s.entry !== '') {
      const r = calcBinary(s.acc, s.op, parseInt(s.entry, 10) || 0);
      return { entry: r > 0 ? String(r) : '', acc: null, op: null };
    }
    return s;
  }
  return s;
}

// 現在の状態から「記録する」で使う金額を求める（＝を押していなくても確定値を計算）
export function calcValue(state) {
  const s = state || CALC_INIT;
  if (s.entry !== '') {
    const b = parseInt(s.entry, 10) || 0;
    return s.acc !== null && s.op ? calcBinary(s.acc, s.op, b) : Math.min(CALC_MAX, b);
  }
  return s.acc !== null ? s.acc : 0;
}

// 途中式の表示文字列（例: '¥1,200 ＋ 800'。式が無ければ ''）
export function calcExprText(state) {
  const s = state || CALC_INIT;
  if (s.acc === null || !s.op) return '';
  const opChar = { '+': '＋', '-': '−', '*': '×', '/': '÷' }[s.op];
  return `${formatYen(s.acc)} ${opChar} ${s.entry || ''}`.trim();
}

// ============================================================
// プラン枠：宿泊・日帰り等の各セットに旅行プランを何件でも並行保存
//   v1.10.0 は固定3枠（slot1〜3）。v1.15.0 で上限撤廃＝➕で1件ずつ追加する方式へ。
//   枠1（slot1）＝従来どおりドキュメント直下の trip / budgets / expenses
//                 → 既存データは何も動かさずそのまま「枠1」として見える（無損失）
//                 → 旧版クライアント（PWA更新遅延）も枠1だけは今までどおり読み書きできる
//   枠2以降      ＝ tripSlots.slotN（N=2,3,4,…。マップ。中身は枠1と同じ3フィールド）
//                 → slot2/slot3 は v1.10.0 と同じ場所＝データ移行ゼロ
// エクスポートの冪等キー tripId：枠1＝setId（従来と同一＝過去の記録行とそのまま一致）、
//   枠N＝`{setId}_sN`（枠2=_s2・枠3=_s3 は v1.10.0 と同一。サーバー側 /^[A-Za-z0-9_-]{1,64}$/ に適合）
// 画面の並び順：旅行日（開始日）が近い順に左から。過ぎた旅行は右側に薄く、日付未入力は右端。
// ============================================================
const SLOT_KEY_RE = /^slot([1-9]\d*)$/;

// スロットキー→枠番号（'slot4'→4）。不正なら null
export function slotNumber(k) {
  const m = typeof k === 'string' ? SLOT_KEY_RE.exec(k) : null;
  return m ? Number(m[1]) : null;
}

// 不正・未設定のスロットキーは枠1に落とす（localStorage の値をそのまま信用しない）
export function normalizeSlotKey(k) {
  return slotNumber(k) ? k : 'slot1';
}

// 枠ごとのエクスポート冪等キー（枠1は従来の setId と完全一致＝後方互換）
export function slotExportId(setId, slotKey) {
  const k = normalizeSlotKey(slotKey);
  if (k === 'slot1') return setId;
  return `${setId}_s${slotNumber(k)}`;
}

// Firestore updateDoc に渡すフィールドパス（枠1＝ルート／枠2以降＝tripSlots 配下）
export function slotFieldPath(slotKey, field) {
  const k = normalizeSlotKey(slotKey);
  return k === 'slot1' ? field : `tripSlots.${k}.${field}`;
}

// trip メタの部分更新パッチ → Firestore updateDoc 用のドットパス（v1.15.1）
//   { tripName: 'X', kind: 'business' } → { 'tripSlots.slot2.trip.tripName': 'X', 'tripSlots.slot2.trip.kind': 'business' }
//   trip 全体を丸ごと書かない＝直前の書き込み（旅行名の onBlur 等）を古い画面状態で上書きして消さない。
//   undefined の値は Firestore が拒否するので落とす（null は「未設定に戻す」として残す）
export function tripPatchFields(slotKey, patch) {
  const tripPath = slotFieldPath(slotKey, 'trip');
  const out = {};
  Object.keys(patch || {}).forEach((k) => {
    if (patch[k] !== undefined) out[`${tripPath}.${k}`] = patch[k];
  });
  return out;
}

// 指定枠のデータを「従来の set と同じ形」のビューで返す。
// 既存の純関数（getTrip / getBudgets / calcOverall / tripDisplayName /
// buildExportPayload 等）がこのビューに対してそのまま使える。
// id は枠ごとのエクスポート冪等キー。枠2以降は tripSlots からのみ読む
// （ルートへのフォールバックはしない＝枠を跨いでデータが混ざらない）
export function getSlotView(set, slotKey) {
  if (!set) return null;
  const k = normalizeSlotKey(slotKey);
  if (k === 'slot1') {
    return { id: set.id, name: set.name, trip: set.trip, budgets: set.budgets, expenses: set.expenses };
  }
  const raw = set.tripSlots && typeof set.tripSlots === 'object' ? set.tripSlots[k] : null;
  const s = raw && typeof raw === 'object' ? raw : {};
  return { id: slotExportId(set.id, k), name: set.name, trip: s.trip, budgets: s.budgets, expenses: s.expenses };
}

// 枠にデータがあるか（チップの表示・空き枠判定用）
export function slotHasData(set, slotKey) {
  const v = getSlotView(set, slotKey);
  if (!v) return false;
  return getTrip(v) !== null || getBudgets(v).length > 0 || getExpenses(v).length > 0;
}

// データが入っている枠のキー一覧（枠番号順）。空の枠は含めない
export function listSlotKeys(set) {
  if (!set) return [];
  const keys = [];
  if (slotHasData(set, 'slot1')) keys.push('slot1');
  const slots = set.tripSlots && typeof set.tripSlots === 'object' ? set.tripSlots : {};
  Object.keys(slots)
    .filter((k) => k !== 'slot1' && slotNumber(k) && slotHasData(set, k))
    .forEach((k) => keys.push(k));
  return keys.sort((a, b) => slotNumber(a) - slotNumber(b));
}

// 次に使える空き枠のキー（枠1から順に最初の空き）。➕で新しいプランを作るときに使う
export function nextFreeSlotKey(set) {
  for (let n = 1; ; n += 1) {
    const k = `slot${n}`;
    if (!slotHasData(set, k)) return k;
  }
}

// 枠チップのラベル：旅行名が設定済みならそれを、なければ「プランN」
export function slotLabel(set, slotKey) {
  const k = normalizeSlotKey(slotKey);
  const v = getSlotView(set, k);
  const trip = getTrip(v);
  const n = trip && typeof trip.tripName === 'string' ? trip.tripName.trim() : '';
  return n || `プラン${slotNumber(k)}`;
}

// 枠の日付状態：'upcoming'（開始日が今日以降）／'past'（終了日（無ければ開始日）が昨日以前）／'undated'（日付未入力）
export function slotStatus(set, slotKey, today) {
  const trip = getTrip(getSlotView(set, slotKey));
  const start = trip && typeof trip.startDate === 'string' && trip.startDate ? trip.startDate : null;
  if (!start) return 'undated';
  const end = trip && typeof trip.endDate === 'string' && trip.endDate ? trip.endDate : start;
  const last = end >= start ? end : start;
  return last < today ? 'past' : 'upcoming';
}

// 表示順に並べたキー配列：①これからの旅行＝開始日が近い順 ②過ぎた旅行（開始日順）③日付未入力（枠番号順）
// ④まだ何も入っていない枠（➕で作った直後）は最後。同じ日付は枠番号順。
// 入力の keys を並べ替えるだけで、データは一切触らない
export function sortSlotKeys(set, keys, today) {
  const rank = { upcoming: 0, past: 1, undated: 2 };
  const info = (k) => {
    const trip = getTrip(getSlotView(set, k));
    const start = trip && typeof trip.startDate === 'string' ? trip.startDate : '';
    const r = slotHasData(set, k) ? rank[slotStatus(set, k, today)] : 3;
    return { k, r, start, n: slotNumber(k) || 0 };
  };
  return keys
    .map(info)
    .sort((a, b) => a.r - b.r || (a.start < b.start ? -1 : a.start > b.start ? 1 : 0) || a.n - b.n)
    .map((x) => x.k);
}

// 画面を開いたときの既定の枠：日付が近いプラン（並び順の先頭）。プランが無ければ枠1
export function defaultSlotKey(set, today) {
  const keys = sortSlotKeys(set, listSlotKeys(set), today);
  return keys[0] || 'slot1';
}

// エクスポート用ペイロードを組み立てる（サーバー側でも再検証される）
export function buildExportPayload(set, master, exportDate) {
  const trip = getTrip(set) || {};
  const rows = buildCompareRows(set, master);
  const tripName = typeof trip.tripName === 'string' && trip.tripName.trim() ? trip.tripName.trim() : null;
  return {
    tripId: set.id,
    recordId: trip.recordId || null,
    name: set.name || '(名称未設定)',
    tripName, // 旅行固有の名前（未設定なら null＝サーバー側でセット名にフォールバック）
    kind: trip.kind === 'business' ? 'business' : 'private',
    startDate: trip.startDate || null,
    endDate: trip.endDate || null,
    exportDate,
    // テーマ（v1.11.0・任意）：未設定は null。サーバー側は一覧行のメモ列（F）に記録する
    mainTheme: normalizeThemeText(trip.mainTheme),
    subTheme: normalizeThemeText(trip.subTheme),
    categories: rows.map((r) => ({
      name: r.name,
      icon: r.icon,
      color: r.color,
      budget: r.budget,
      actual: r.actual,
    })),
  };
}

// ============================================================
// 旅行日（v1.12.2）：開始日を変えたときの終了日の扱い
//   宿泊予約サイトと同じく「終了日が開始日より前」を作らせない。
//   逆転する場合は終了日を消して選び直してもらう（黙って開始日に合わせない）。
//   日付は 'YYYY-MM-DD' 固定なので文字列比較で大小が正しく出る。
// ============================================================
export function nextEndDate(startDate, endDate) {
  const s = typeof startDate === 'string' && startDate ? startDate : null;
  const e = typeof endDate === 'string' && endDate ? endDate : null;
  if (!s) return e; // 開始日を消しただけなら終了日はそのまま
  if (!e) return null;
  return e < s ? null : e;
}

// ============================================================
// 期間カレンダー（v1.12.3）：開始日〜終了日を帯で見せるための純ロジック
//   航空会社の予約カレンダーと同じ体験にする。
//   日付は 'YYYY-MM-DD' 固定文字列で扱う（文字列比較で前後関係が正しく出る）。
// ============================================================

// 'YYYY-MM-DD' → { y, m, d }。壊れていたら null
export function parseDateStr(s) {
  if (typeof s !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return { y, m: mo, d };
}

// y, m, d → 'YYYY-MM-DD'
export function toDateStr(y, m, d) {
  const p2 = (n) => String(n).padStart(2, '0');
  return `${y}-${p2(m)}-${p2(d)}`;
}

// 月を進める・戻す（m は 1〜12）
export function addMonths(y, m, delta) {
  const total = y * 12 + (m - 1) + delta;
  return { y: Math.floor(total / 12), m: (total % 12) + 1 };
}

// カレンダーの升目（日曜始まり・6週×7日＝42マス固定＝月をまたいでも高さが変わらない）
// 各マス: { date: 'YYYY-MM-DD', day: 日, inMonth: その月かどうか }
export function buildMonthGrid(y, m) {
  const first = new Date(y, m - 1, 1);
  const startOffset = first.getDay(); // 0=日
  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const dt = new Date(y, m - 1, 1 - startOffset + i);
    cells.push({
      date: toDateStr(dt.getFullYear(), dt.getMonth() + 1, dt.getDate()),
      day: dt.getDate(),
      inMonth: dt.getMonth() + 1 === m && dt.getFullYear() === y,
    });
  }
  return cells;
}

// そのマスが期間のどこにあたるか。'start' | 'end' | 'single' | 'middle' | null
// single＝開始日だけ決まっている／開始と終了が同じ日
export function rangeCellKind(dateStr, start, end) {
  if (!dateStr || !start) return null;
  if (!end) return dateStr === start ? 'single' : null;
  if (start === end) return dateStr === start ? 'single' : null;
  if (dateStr === start) return 'start';
  if (dateStr === end) return 'end';
  return dateStr > start && dateStr < end ? 'middle' : null;
}

// マスをタップしたときの次の選択状態（航空券サイトと同じ挙動）
//   ・開始日が無い／両方決まっている → その日を新しい開始日にして終了日は空に戻す
//   ・開始日だけある → 開始日以降なら終了日に確定、開始日より前なら開始日を置き換える
export function pickRange(start, end, tapped) {
  if (!tapped) return { startDate: start || null, endDate: end || null };
  if (!start || end) return { startDate: tapped, endDate: null };
  if (tapped < start) return { startDate: tapped, endDate: null };
  return { startDate: start, endDate: tapped };
}
